import axiosInstance from '@/libs/axios_config'
import { useMutation } from '@tanstack/react-query'
import { z } from 'zod'

// Define the schema for a single file response
const FileResponseSchema = z.object({
  file_name: z.string(),
  // Add other fields as needed based on your API response
})

// Type for a validated file response
type FileResponse = z.infer<typeof FileResponseSchema>

// Function to upload a single file
const uploadSingleFile = async (file: File): Promise<FileResponse> => {
  const formData = new FormData()
  formData.append('file', file)

  const response = await axiosInstance.post('/upload-temp-file', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  // Validate the response data against the schema
  return FileResponseSchema.parse(response.data)
}

// Define types for better TypeScript support
type UploadResult = FileResponse[]
type UploadError = Error

export const useTemporaryFileUpload = (options?: {
  onSuccess?: (data: UploadResult) => void
  onError?: (error: UploadError) => void
}) => {
  const uploadMutation = useMutation({
    mutationFn: async (files: File[]) => {
      // Upload each file in parallel using Promise.all
      const results = await Promise.all(
        files.map((file) => uploadSingleFile(file))
      )

      return results
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  })

  return uploadMutation
}
